"use client";

import cn from "classnames";
import Image from "next/image";
import Link from "next/link";
import { useState, useEffect, useMemo } from "react";

import CircularProgress from "@mui/material/CircularProgress";
import CheckIcon from "@mui/icons-material/Check";
import List from "@mui/material/List";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Typography from "@mui/material/Typography";

import { BasePopper } from "@/src/component/base/BasePopper";
import { Preload } from "@/src/component/partial/Preload";
import { ProviderHooks } from "@/src/hook";
import { StrapiCollectionType } from "@/src/type";

import styles from "./I18NDropdown.module.scss";

interface I18NDropdownProps {
  placement?: "bottom-start" | "bottom-end";
  color?: "dark" | "light";
  drawer?: boolean;
  domainData?: StrapiCollectionType.Domain;
}

function I18NDropdown({ placement, color, drawer, domainData }: I18NDropdownProps) {
  const [popperOpen, setPopperOpen] = useState<boolean>(false);
  const [waiting, setWaiting] = useState<string | undefined>(undefined);
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const cms = ProviderHooks.useCMS();
  const locales = useMemo(() => ((domainData?.i18n ?? []).length ? domainData?.i18n : []), [domainData?.i18n]);

  useEffect(() => {
    setWaiting(undefined);
  }, [cms.locale]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setPopperOpen(false);
    }, 150);
    return () => clearTimeout(timeout);
  }, [cms.locale]);

  const handlePopper = () => {
    setPopperOpen((popperOpen) => !popperOpen);
  };

  function getColorClass() {
    if (!color) {
      return undefined;
    }
    return color === "dark" ? styles.dark : styles.light;
  }

  function getIcon() {
    if (!color) {
      return "/image/icon_dropdown_arrow.svg";
    }
    return color === "dark" ? "/image/icon_dropdown_arrow_dark.svg" : "/image/icon_dropdown_arrow_light.svg";
  }

  function IconToShow({ locale }: { locale: string }) {
    if (waiting === locale) {
      return (
        <CircularProgress
          color="primary"
          size={20}
        />
      );
    } else if (cms.locale === locale) {
      return <CheckIcon color="primary" />;
    }
    return <></>;
  }

  if ((locales ?? []).length === 0) {
    return <></>;
  }

  return (
    <>
      <Preload images={["/image/icon_dropdown_arrow.svg", "/image/icon_dropdown_arrow_light.svg", "/image/icon_dropdown_arrow_dark.svg"]} />
      <div
        className={cn(styles.dropdownContainer, getColorClass())}
        ref={(el) => setAnchorEl(el)}
        onClick={handlePopper}
      >
        <p className={cn(styles.localeSelected, getColorClass())}>{cms.locale}</p>
        <div className={styles.dropdownIcon}>
          <Image
            src={getIcon()}
            width={24}
            height={24}
            alt="Open Internationalization Dropdown"
          />
        </div>
      </div>
      <BasePopper
        popperProps={{
          placement: placement ?? "bottom",
          open: popperOpen,
          anchorEl: anchorEl,
          sx: {
            paddingTop: "4px",
            zIndex: drawer ? "var(--zIndexI18NDropdownInDrawer)" : "var(--zIndexI18NDropdown)",
          },
        }}
        closeOnBackgroundClick={() => setPopperOpen(false)}
      >
        <div className={cn(styles.popperInner, getColorClass())}>
          <Typography
            variant="tiny"
            className={cn(styles.popperTitle, styles.dark)}
          >
            Language
          </Typography>
          <List component="nav">
            {(locales ?? []).map((opt) => (
              <Link
                key={opt.locale}
                id={`locale_${opt.locale}`}
                href={cms.changeLocale(opt.locale)}
                onClick={() => setWaiting(opt.locale)}
              >
                <ListItemButton className={styles.languageItem}>
                  <div className={styles.languageItemInner}>
                    <ListItemIcon>{opt.locale}</ListItemIcon>
                    <ListItemText primary={<Typography variant="body2">{opt.label}</Typography>} />
                  </div>
                  <IconToShow locale={opt.locale} />
                </ListItemButton>
              </Link>
            ))}
          </List>
        </div>
      </BasePopper>
    </>
  );
}

export default I18NDropdown;
